// Considered: ArcActionExecutionListMolecule — that renders runtime execution status; this is the
// editor the inspector rail opens for `selection.kind === 'intent'`, with its own name/stage/
// priority fields, a perspective picker for "who it names" and two condition builders (fulfillment,
// prerequisites). Considered: TypeFieldRowMolecule alone — reused for every labelled field, but the
// editor also carries a read-back sentence, section headings and the perspective picker + condition
// builders it has no slot for. See spec Reuse Audit (arc-editor-canvas-v1.md).
import React from 'react';
import { useTranslation } from 'localization';
import type { Button } from 'primereact/button';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TypeFieldRowMolecule from '../studio/TypeFieldRowMolecule';
import InlineConfirmMolecule from '../studio/InlineConfirmMolecule';
import { focusPrimeButton } from '../../../utils/focusPrimeButton';
import PerspectiveMultiPickerMolecule from '../PerspectiveMultiPicker/PerspectiveMultiPickerMolecule';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import type { PerspectiveCatalogGroup } from '../../../utils/filterPropertyBuilder';
import type { FilterGroup } from 'types/filters';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import './ArcIntentInspectorMolecule.scss';

export type ArcIntentPriority = 'high' | 'medium' | 'low';

export interface ArcIntentInspectorSelectOption {
  value: string;
  label: string;
}

export interface ArcIntentInspectorMoleculeProps {
  /** The selected intent's stage name, read into the summary sentence. */
  stageName: string;
  /** The named perspectives' labels joined for the summary sentence ("Alice or Bob"), or the
   * `nobody` translation when `perspectives` is empty. */
  whoLabel: string;
  name: string;
  onNameChange: (value: string) => void;
  stageId: string;
  onStageChange: (value: string) => void;
  stageOptions: ArcIntentInspectorSelectOption[];
  priority: ArcIntentPriority;
  onPriorityChange: (priority: ArcIntentPriority) => void;
  /** Selected perspective system names, or `[PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED]`. */
  perspectives: string[];
  onPerspectivesChange: (value: string[]) => void;
  perspectiveGroups: PerspectiveCatalogGroup[];
  perspectivesLoading?: boolean;
  perspectivesError?: string | null;
  /** Labels of named perspectives that are not on the sphere type's base schema
   * (`AC_PERSPECTIVES` entries with `base === false`) — empty hides the warning. */
  offBaseSchemaLabels: string[];
  fulfillmentCondition?: FilterGroup;
  onFulfillmentConditionChange: (group: FilterGroup) => void;
  prerequisites?: FilterGroup;
  onPrerequisitesChange: (group: FilterGroup) => void;
  conditionAvailableProperties: PropertyOption[];
  editable: boolean;
  onApply: () => void;
  /** Called only after the inline confirm is accepted. */
  onRemove: () => void;
  className?: string;
}

/** The intent editor the inspector rail's `selection.body` hosts for
 * `selection.kind === 'intent'` (`arc-inspector.jsx:494-559`, `IntentEditor`). */
const ArcIntentInspectorMolecule: React.FC<ArcIntentInspectorMoleculeProps> = ({
  stageName,
  whoLabel,
  name,
  onNameChange,
  stageId,
  onStageChange,
  stageOptions,
  priority,
  onPriorityChange,
  perspectives,
  onPerspectivesChange,
  perspectiveGroups,
  perspectivesLoading = false,
  perspectivesError = null,
  offBaseSchemaLabels,
  fulfillmentCondition,
  onFulfillmentConditionChange,
  prerequisites,
  onPrerequisitesChange,
  conditionAvailableProperties,
  editable,
  onApply,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'studio.arcEditor.inspector.intent';
  // Remove is consequential (the spec's reversal table) — the inline confirm states the effect in
  // place; "Keep" returns focus to the button that opened it.
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const removeButtonRef = React.useRef<Button>(null);
  const keepIntent = () => {
    setConfirmingRemove(false);
    focusPrimeButton(removeButtonRef.current);
  };
  const [nameTouched, setNameTouched] = React.useState(false);
  const nameError = nameTouched && !name.trim() ? t(`${ns}.nameRequired`) : undefined;

  return (
    <div
      className={`arc-intent-inspector ${className}`.trim()}
      data-datum="side.panel#4"
      data-kind="panel"
      data-container="side"
    >
      <div className="arc-intent-inspector__body">
        <p className="arc-intent-inspector__sentence" data-datum="side.at" data-kind="callout">
          {t(`${ns}.sentencePrefix`, { stage: stageName, who: whoLabel })}
          <strong data-datum="side.i-name" data-kind="strong">
            {name}
          </strong>
          {t(`${ns}.sentenceSuffix`)}
        </p>

        <TypeFieldRowMolecule
          label={t(`${ns}.nameLabel`)}
          hint={t(`${ns}.nameHint`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          kind="field"
          datum="side.children#8"
          labelDatum="side.label#19"
          errorMessage={nameError}
          controls={[
            {
              kind: 'text',
              value: name,
              onChange: onNameChange,
              onCommit: () => setNameTouched(true),
              ariaLabel: t(`${ns}.nameLabel`),
              datum: 'side.input#2',
              datumKind: 'input',
            },
          ]}
        />

        <div className="arc-intent-inspector__two">
          <TypeFieldRowMolecule
            label={t(`${ns}.stageLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.stage"
            controls={[
              {
                kind: 'select',
                value: stageId,
                options: stageOptions,
                onChange: onStageChange,
                ariaLabel: t(`${ns}.stageLabel`),
                datumKind: 'select',
              },
            ]}
          />
          <TypeFieldRowMolecule
            label={t(`${ns}.priorityLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.priority"
            controls={[
              {
                kind: 'select',
                value: priority,
                options: [
                  { value: 'high', label: t(`${ns}.priorityHigh`) },
                  { value: 'medium', label: t(`${ns}.priorityMedium`) },
                  { value: 'low', label: t(`${ns}.priorityLow`) },
                ],
                onChange: (value) => onPriorityChange(value as ArcIntentPriority),
                ariaLabel: t(`${ns}.priorityLabel`),
                datumKind: 'select',
              },
            ]}
          />
        </div>

        <section className="arc-intent-inspector__section" data-container="side > panel#4 > group:children">
          <span className="arc-intent-inspector__section-label" data-datum="side.label#20" data-kind="heading">
            {t(`${ns}.whoItNamesHeading`)}
          </span>
          <PerspectiveMultiPickerMolecule
            value={perspectives}
            groups={perspectiveGroups}
            onChange={onPerspectivesChange}
            disabled={!editable}
            loading={perspectivesLoading}
            error={perspectivesError}
          />
          <p className="arc-intent-inspector__note" data-datum="side.holding-a-perspective-is-what-makes-this" data-kind="note">
            {t(`${ns}.namesNote`)}
          </p>
          {offBaseSchemaLabels.length > 0 && (
            <p className="arc-intent-inspector__note arc-intent-inspector__note--warn" data-datum="side.are-not" data-kind="note">
              <GlyphAtom glyph="lucide:help-circle" aria-hidden />
              <strong data-datum="side.i-perspectives-filter-p-ac-perspectives-" data-kind="strong">
                {offBaseSchemaLabels.join(', ')}
              </strong>{' '}
              {offBaseSchemaLabels.length > 1 ? t(`${ns}.offBaseSchemaNoteMany`) : t(`${ns}.offBaseSchemaNoteOne`)}
            </p>
          )}
        </section>

        <section className="arc-intent-inspector__section" data-container="side > panel#4 > group:children">
          <span className="arc-intent-inspector__section-label" data-datum="side.label#21" data-kind="heading">
            {t(`${ns}.countsAsDoneHeading`)}
          </span>
          <UnifiedFilterTreeMolecule
            value={fulfillmentCondition}
            onChange={onFulfillmentConditionChange}
            availableProperties={conditionAvailableProperties}
            framed={false}
            readsAs
            subject={t(`${ns}.fulfilledLabel`)}
            datumScope="side"
            cardDatumId="side.label#22"
            readOnly={!editable}
          />
        </section>

        <section className="arc-intent-inspector__section" data-container="side > panel#4 > group:children">
          <span className="arc-intent-inspector__section-label" data-datum="side.label#23" data-kind="heading">
            {t(`${ns}.blockedUntilHeading`)}
          </span>
          <UnifiedFilterTreeMolecule
            value={prerequisites}
            onChange={onPrerequisitesChange}
            availableProperties={conditionAvailableProperties}
            framed={false}
            readsAs
            subject={t(`${ns}.blockedLabel`)}
            datumScope="side"
            cardDatumId="side.label#24"
            readOnly={!editable}
          />
        </section>

        <p className="arc-intent-inspector__note" data-datum="side.each-viewer-sees-this-intent" data-kind="note">
          {t(`${ns}.viewerReadingNote`)}
        </p>
      </div>
      {editable && confirmingRemove && (
        <InlineConfirmMolecule
          tone="danger"
          testId="arc-intent-inspector-remove-confirm"
          titleId="arc-intent-inspector-remove-confirm-title"
          title={t(`${ns}.removeConfirm.title`, { name })}
          body={t(`${ns}.removeConfirm.body`, { stage: stageName })}
          actions={[
            {
              label: t(`${ns}.removeConfirm.accept`),
              onClick: onRemove,
              variant: 'danger',
              'data-testid': 'arc-intent-inspector-remove-confirm-accept',
            },
            {
              label: t(`${ns}.removeConfirm.keep`),
              onClick: keepIntent,
              variant: 'text',
              'data-testid': 'arc-intent-inspector-remove-confirm-dismiss',
            },
          ]}
        />
      )}
      {editable && (
        <div className="arc-intent-inspector__actions" data-container="side > actions">
          <ButtonAtom variant="secondary" size="small" onClick={onApply} data-datum="side.apply#3" data-kind="button">
            {t(`${ns}.apply`)}
          </ButtonAtom>
          <ButtonAtom
            variant="danger"
            size="small"
            icon={<GlyphAtom glyph="lucide:trash-2" aria-hidden />}
            ref={removeButtonRef}
            onClick={() => setConfirmingRemove(true)}
            data-datum="side.remove-intent"
            data-kind="button"
          >
            {t(`${ns}.removeIntent`)}
          </ButtonAtom>
        </div>
      )}
    </div>
  );
};

ArcIntentInspectorMolecule.displayName = 'ArcIntentInspectorMolecule';

export default ArcIntentInspectorMolecule;
